import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AzumayaDocument.css';
import {
  FolderOpen,
  File,
  Download,
  Eye,
  ChevronDown,
  ChevronRight,
  Loader,
  AlertCircle,
  FileText,
  FileSpreadsheet,
  Presentation,
  Archive
} from 'lucide-react';

const AzumayaDocument = () => {
  const [yearsData, setYearsData] = useState([]); // { year, folders, expanded, loading }
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewFile, setPreviewFile] = useState(null); // { filename, year, folder, extension }

  // Fetch years on component mount
  useEffect(() => {
    fetchYears();
  }, []);

  const fetchYears = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/file-management/years');
      const yearsArray = response.data.map(year => ({
        year,
        folders: [],
        expanded: false,
        loading: false
      }));
      setYearsData(yearsArray);
    } catch (err) {
      setError('Failed to load years');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle year expansion and fetch folders
  const toggleYear = async (year) => {
    try {
      setYearsData(prevData => {
        return prevData.map(item => {
          if (item.year === year) {
            return {
              ...item,
              expanded: !item.expanded,
              loading: !item.expanded && item.folders.length === 0 ? true : item.loading
            };
          }
          return item;
        });
      });

      // Fetch folders if not already loaded
      const yearItem = yearsData.find(y => y.year === year);
      if (!yearItem.expanded && yearItem.folders.length === 0) {
        try {
          const response = await axios.get(`/file-management/folders/${year}`);
          setYearsData(prevData =>
            prevData.map(item =>
              item.year === year
                ? {
                    ...item,
                    folders: response.data,
                    loading: false
                  }
                : item
            )
          );
        } catch (err) {
          console.error('Error fetching folders:', err);
          setYearsData(prevData =>
            prevData.map(item =>
              item.year === year ? { ...item, loading: false } : item
            )
          );
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch files when folder is selected
  const selectFolder = async (year, folder) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedFolder(folder);
      setSelectedYear(year);
      setFiles([]);

      const response = await axios.get(`/file-management/files/${year}/${folder}`);
      setFiles(response.data);
    } catch (err) {
      setError('Failed to load files');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (filename) => {
    if (!selectedYear || !selectedFolder) return;
    window.location.href = `/az_document/${selectedYear}/${selectedFolder}/${filename}`;
  };

  const viewFile = (filename) => {
    if (!selectedYear || !selectedFolder) return;
    const ext = filename.split('.').pop().toLowerCase();
    setPreviewFile({
      filename,
      year: selectedYear,
      folder: selectedFolder,
      extension: ext
    });
  };

  const canPreviewFile = (ext) => {
    const previewable = ['pdf', 'pptx', 'ppt', 'xlsx', 'xls', 'docx', 'doc', 'mp4', 'webm', 'ogg', 'avi', 'mov'];
    return previewable.includes(ext.toLowerCase());
  };

  const getPreviewUrl = (year, folder, filename, ext) => {
    // For local files, use direct path
    return `/az_document/${year}/${folder}/${filename}`;
  };

  const getFileIcon = (extension) => {
    const ext = extension.toLowerCase();
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    } else if (['doc', 'docx', 'txt'].includes(ext)) {
      return <FileText className="w-5 h-5 text-blue-600" />;
    } else if (['ppt', 'pptx'].includes(ext)) {
      return <Presentation className="w-5 h-5 text-red-600" />;
    } else if (['zip', 'rar', '7z'].includes(ext)) {
      return <Archive className="w-5 h-5 text-orange-600" />;
    } else if (['pdf'].includes(ext)) {
      return <FileText className="w-5 h-5 text-red-500" />;
    }
    return <File className="w-5 h-5 text-gray-600" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="azumaya-document-container">
      {/* Left Sidebar - Years with Dropdown Folders */}
      <div className="left-sidebar">
        <div className="sidebar-header">
          <h2>Documents</h2>
        </div>
        <div className="years-list">
          {loading && yearsData.length === 0 ? (
            <div className="loading-message">
              <Loader className="w-6 h-6 animate-spin" />
              <p>Loading...</p>
            </div>
          ) : error && yearsData.length === 0 ? (
            <div className="error-message">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          ) : yearsData.length === 0 ? (
            <div className="empty-message">
              <p>No years found</p>
            </div>
          ) : (
            yearsData.map((yearItem) => (
              <div key={yearItem.year} className="year-group">
                {/* Year Header - Clickable to expand */}
                <div
                  className="year-header"
                  onClick={() => toggleYear(yearItem.year)}
                >
                  <div className="year-toggle">
                    {yearItem.expanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                    <span className="year-label">{yearItem.year}</span>
                  </div>
                  {yearItem.loading && (
                    <Loader className="w-4 h-4 animate-spin" />
                  )}
                </div>

                {/* Folders List - Shows when year is expanded */}
                {yearItem.expanded && (
                  <div className="folders-list">
                    {yearItem.loading ? (
                      <div className="folder-loading">
                        <Loader className="w-4 h-4 animate-spin" />
                        <span>Loading folders...</span>
                      </div>
                    ) : yearItem.folders.length === 0 ? (
                      <div className="folder-empty">No folders</div>
                    ) : (
                      yearItem.folders.map((folder) => (
                        <div
                          key={folder}
                          className={`folder-item-sidebar ${
                            selectedFolder === folder &&
                            selectedYear === yearItem.year
                              ? 'active'
                              : ''
                          }`}
                          onClick={() =>
                            selectFolder(yearItem.year, folder)
                          }
                        >
                          <FolderOpen className="w-4 h-4" />
                          <span className="folder-name-sidebar">
                            {folder}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Section - Files */}
      <div className="right-section">
        <div className="section-header">
          <h2>Files</h2>
          {selectedFolder && selectedYear && (
            <span className="folder-badge">
              {selectedYear} / {selectedFolder}
            </span>
          )}
        </div>
        <div className="files-list">
          {loading && files.length === 0 ? (
            <div className="loading-message">
              <Loader className="w-6 h-6 animate-spin" />
              <p>Loading files...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          ) : !selectedFolder ? (
            <div className="empty-message">
              <p>Select a folder to view files</p>
            </div>
          ) : files.length === 0 ? (
            <div className="empty-message">
              <p>No files in this folder</p>
            </div>
          ) : (
            <table className="files-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Modified</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file, index) => (
                  <tr key={index} className="file-row">
                    <td className="file-name-cell">
                      <div className="file-name-content">
                        {getFileIcon(file.extension)}
                        <span className="file-name">{file.name}</span>
                      </div>
                    </td>
                    <td className="file-type-cell">{file.type}</td>
                    <td className="file-size-cell">
                      {formatFileSize(file.size)}
                    </td>
                    <td className="file-date-cell">
                      {formatDate(file.modified)}
                    </td>
                    <td className="file-action-cell">
                      <div className="action-buttons">
                        {canPreviewFile(file.extension) && (
                          <button
                            className="view-btn"
                            onClick={() => viewFile(file.name)}
                            title="View file"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          className="download-btn"
                          onClick={() => downloadFile(file.name)}
                          title="Download file"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <div className="preview-modal-overlay" onClick={() => setPreviewFile(null)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>{previewFile.filename}</h3>
              <button
                className="close-btn"
                onClick={() => setPreviewFile(null)}
              >
                ✕
              </button>
            </div>
            <div className="preview-modal-content">
              {['pdf'].includes(previewFile.extension) && (
                <iframe
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(
                    window.location.origin +
                      getPreviewUrl(
                        previewFile.year,
                        previewFile.folder,
                        previewFile.filename,
                        previewFile.extension
                      )
                  )}&embedded=true`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="PDF Viewer"
                />
              )}

              {['pptx', 'ppt'].includes(previewFile.extension) && (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    window.location.origin +
                      getPreviewUrl(
                        previewFile.year,
                        previewFile.folder,
                        previewFile.filename,
                        previewFile.extension
                      )
                  )}`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="PowerPoint Viewer"
                />
              )}

              {['xlsx', 'xls'].includes(previewFile.extension) && (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    window.location.origin +
                      getPreviewUrl(
                        previewFile.year,
                        previewFile.folder,
                        previewFile.filename,
                        previewFile.extension
                      )
                  )}`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="Excel Viewer"
                />
              )}

              {['docx', 'doc'].includes(previewFile.extension) && (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    window.location.origin +
                      getPreviewUrl(
                        previewFile.year,
                        previewFile.folder,
                        previewFile.filename,
                        previewFile.extension
                      )
                  )}`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="Word Viewer"
                />
              )}

              {['mp4', 'webm', 'ogg', 'avi', 'mov'].includes(
                previewFile.extension
              ) && (
                <video
                  width="100%"
                  height="100%"
                  controls
                  controlsList="nodownload"
                >
                  <source
                    src={getPreviewUrl(
                      previewFile.year,
                      previewFile.folder,
                      previewFile.filename,
                      previewFile.extension
                    )}
                    type={`video/${previewFile.extension === 'avi' ? 'x-msvideo' : previewFile.extension}`}
                  />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AzumayaDocument;
